// Reading a day from the local database, and asking the server to prepare days.

import { useLiveQuery } from "dexie-react-hooks";
import { pickTemplateId } from "@shared/rhythm";
import { api, isNetworkError } from "./api";
import { db, type DayBlock, type DayPlan, type DayTemplateBlock } from "./db";
import { minutesToTime, timeToMinutes } from "@shared/time";
import { sync } from "./sync";

/** Asks the server to create today and tomorrow (only the server places things on a day, spec L35). */
export async function ensureDays(from: string): Promise<void> {
  try {
    const { created } = await api<{ created: string[] }>("/days/ensure", { body: { from, days: 2 } });
    if (created.length) await sync.sync();
  } catch (error) {
    if (!isNetworkError(error)) console.error("Preparing days failed", error);
  }
}

export type DayView =
  | { kind: "loading" }
  | { kind: "plan"; plan: DayPlan; blocks: DayBlock[] }
  | { kind: "preview"; templateName: string | null; blocks: DayTemplateBlock[] };

/** The day as stored, or (offline, not created yet) a read only preview from the local day types. */
export function useDay(date: string): DayView {
  const view = useLiveQuery(async (): Promise<DayView> => {
    const plan = await db.day_plans
      .where("plan_date")
      .equals(date)
      .filter((p) => !p.deleted_at)
      .first();
    if (plan) {
      const blocks = await db.day_blocks
        .where("day_plan_id")
        .equals(plan.id)
        .filter((b) => !b.deleted_at)
        .toArray();
      return { kind: "plan", plan, blocks };
    }
    const [templates, rules, overrides] = await Promise.all([
      db.day_templates.toArray(),
      db.day_template_rules.toArray(),
      db.day_overrides.toArray(),
    ]);
    const templateId = pickTemplateId(date, templates, rules, overrides);
    const template = templates.find((t) => t.id === templateId);
    const blocks = templateId
      ? await db.day_template_blocks
          .where("template_id")
          .equals(templateId)
          .filter((b) => !b.deleted_at)
          .toArray()
      : [];
    return { kind: "preview", templateName: template?.name ?? null, blocks };
  }, [date]);
  return view ?? { kind: "loading" };
}

export async function setBlockStatus(block: DayBlock, status: DayBlock["status"]): Promise<void> {
  const at = new Date().toISOString();
  await sync.mutate("dayBlock.setStatus", { id: block.id, status, at }, [block.id], async () => {
    await db.day_blocks.update(block.id, { status, completed_at: status === "done" ? at : null });
  });
}

/** "Ik loop achter", confirmed: these blocks of today go `minutes` later (V5). Fixed blocks are never among them. */
export async function shiftBlocks(date: string, blocks: DayBlock[], minutes: number): Promise<void> {
  const later = (time: string) => minutesToTime(Math.min(timeToMinutes(time) + minutes, 23 * 60 + 59));
  const open = blocks.filter((b) => b.status === "open");
  await sync.mutate(
    "dayBlocks.shift",
    { date, ids: blocks.map((b) => b.id), minutes },
    blocks.map((b) => b.id),
    async () => {
      for (const b of open) {
        await db.day_blocks.update(b.id, {
          start_local: `${later(b.start_local)}:00`,
          end_local: b.end_local ? `${later(b.end_local)}:00` : null,
        });
      }
    },
  );
}
